import { STORE_NAME } from '@intigral/constant';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';

import { getCountryPPVEvents } from '../../../../utils/user';
import { useCountryCode } from '../../../../hooks/useConfiguration';
import h from '../../../../helpers/common';

type UsePPVParams = {
    ppvEventConfig: any;
    operators: any;
};

export const usePPV = ({ ppvEventConfig, operators }: UsePPVParams) => {
    const countryCode = useCountryCode();

    const getPPVEventForChannel = (channel) => {
        if (!channel?.isPPV || !ppvEventConfig || !operators) {
            return null;
        }

        const ppvEvents = getCountryPPVEvents(ppvEventConfig, operators, countryCode);
        const channelSubscriptionGuids = channel?.subscriptions?.map((sub) => sub.subscriptionGUID) || [];

        // Find the PPV event that matches this channel's subscription
        const ppvEvent = ppvEvents.find((event) => channelSubscriptionGuids.includes(event.subscriptionGuid));

        return ppvEvent || null;
    };

    /**
     * Helper function to check if user is subscribed to a PPV event
     */
    const isPPVEventPurchased = (ppvEvent) => {
        if (!ppvEvent) return false;

        const userInfo = get(STORE_NAME.USER);
        const userPpvList = userInfo?.ppvList || [];

        // Fallback: Try to get from localStorage cache if not in userInfo
        let ppvSubscribedList = userPpvList;
        if (!ppvSubscribedList || ppvSubscribedList.length === 0) {
            const cachedUser = get('user');
            ppvSubscribedList = cachedUser?.ppvList || [];
        }

        return ppvSubscribedList.some((userPpv) => {
            return (
                userPpv.subscriptionGuid === ppvEvent.subscriptionGuid ||
                userPpv.guid === ppvEvent.subscriptionGuid ||
                userPpv.eventId === ppvEvent.eventId
            );
        });
    };

    /**
     * Helper function to enhance a channel with PPV information
     * This ensures consistent PPV data regardless of where the channel comes from
     */
    const enhanceChannelWithPPV = (channel) => {
        if (!channel) return channel;

        let enhancedChannel = { ...channel };

        if (ppvEventConfig && operators) {
            const ppvEvents = getCountryPPVEvents(ppvEventConfig, operators, countryCode);

            let hasPPV = false;

            for (const sub of channel?.subscriptions || []) {
                for (const event of ppvEvents) {
                    if (sub.subscriptionGUID === event.subscriptionGuid) {
                        hasPPV = true;
                        break;
                    }
                }
                if (hasPPV) break;
            }

            enhancedChannel = {
                ...channel,
                isPPV: !!hasPPV,
            };
        }

        return enhancedChannel;
    };

    // PPV purchase handler: navigates to /<lang>/?eventId=<eventId>
    const handlePPVPurchase = (eventId) => {
        const currentLang = h.getLang ? h.getLang() : 'en';
        const origin = window.location.origin;
        const newUrl = `${origin}/${currentLang}?eventId=${eventId}`;
        window.location.href = newUrl;
    };

    return {
        getPPVEventForChannel,
        isPPVEventPurchased,
        enhanceChannelWithPPV,
        handlePPVPurchase,
    };
};
